import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { useEmailLogs } from '@/hooks/useApi';
import PageSkeleton from '@/components/PageSkeleton';

export default function EmailsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const debouncedSearch = searchParams.get('search') || '';
  const [search, setSearch] = useState(debouncedSearch);
  const documentType = searchParams.get('documentType') || '';
  const opened = searchParams.get('opened') || '';
  const page = Number(searchParams.get('page')) || 1;

  const queryParams: Record<string, unknown> = { page };
  if (debouncedSearch) queryParams.search = debouncedSearch;
  if (documentType) queryParams.documentType = documentType;
  if (opened) queryParams.opened = opened;

  const { data, isLoading } = useEmailLogs(queryParams);
  const emails = data?.data || [];
  const meta = data?.meta;

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const updateParams = (updates: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v) params.set(k, v);
      else params.delete(k);
    });
    if (updates.page === undefined) params.delete('page');
    setSearchParams(params);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateParams({ search: value });
    }, 300);
  };

  const formatDate = (d: string | null) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (isLoading) return <PageSkeleton />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('emails.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('emails.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="input w-64"
        />
        <select
          value={documentType}
          onChange={(e) => updateParams({ documentType: e.target.value })}
          className="input w-40"
        >
          <option value="">{t('emails.allTypes')}</option>
          <option value="invoice">{t('emails.invoice')}</option>
          <option value="quote">{t('emails.quote')}</option>
        </select>
        <select
          value={opened}
          onChange={(e) => updateParams({ opened: e.target.value })}
          className="input w-40"
        >
          <option value="">{t('emails.allStatuses')}</option>
          <option value="1">{t('emails.openedStatus')}</option>
          <option value="0">{t('emails.notOpened')}</option>
        </select>
      </div>

      {/* Table */}
      {emails.length === 0 ? (
        <div className="text-center py-16">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('emails.noEmails')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('emails.noEmailsSubtitle')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('emails.document')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('emails.recipient')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('emails.subject')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('emails.sentAt')}</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('emails.opened')}</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('emails.openCount')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('emails.lastOpened')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {emails.map((email) => (
                <tr key={email.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        email.documentType === 'invoice'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {t(`emails.${email.documentType}`)}
                      </span>
                      <Link
                        to={`/${email.documentType}s/${email.documentId}`}
                        className="text-sm font-medium text-primary-600 hover:text-primary-800"
                      >
                        {email.documentNumber}
                      </Link>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{email.recipientEmail}</div>
                    {email.clientName && (
                      <div className="text-xs text-gray-500">{email.clientName}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700 line-clamp-1">{email.subject}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(email.sentAt)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {email.openedAt ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-500">
                    {email.openCount}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(email.lastOpenedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {meta && meta.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-700">
                {t('common.showing')}{' '}
                {((meta.page - 1) * meta.limit) + 1}-{Math.min(meta.page * meta.limit, meta.total)}
                {' / '}{meta.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => updateParams({ page: String(meta.page - 1) })}
                  disabled={meta.page <= 1}
                  className="btn btn-secondary text-sm disabled:opacity-50"
                >
                  &laquo;
                </button>
                <button
                  onClick={() => updateParams({ page: String(meta.page + 1) })}
                  disabled={meta.page >= meta.pages}
                  className="btn btn-secondary text-sm disabled:opacity-50"
                >
                  &raquo;
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
